// EventChip — чип события во временной сетке (TimeGridDay/TimeGridWeek).
// Позиционирование — absolute-% (top/height/left/width из PositionedSpan, W1).
// state-стилизация: CANCELED приглушён (opacity 0.5 + перечёркнут, aria-label «отменено»);
// пунктирные границы — на стыке локальной полночи (фрагмент-продолжение, §6.3/§17).
import { Box, Link as MuiLink, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import LinkIcon from '@mui/icons-material/Link';
import type { CalendarEvent } from '@/api/calendar';
import { MINUTES_PER_DAY } from '@/lib/timeGridLayout';
import type { PositionedSpan } from '@/lib/timeGridLayout';

interface EventChipProps {
  /** Позиция чипа в колонке дня (из layoutDaySpans). */
  span: PositionedSpan;
  /** Колбэк клика по чипу (переход к detail события). */
  onEventClick?: ((event: CalendarEvent) => void) | undefined;
}

/** Локальные минуты дня (0..1439) — Р1, не UTC-эпоха. */
function localMinutesOf(date: Date): number {
  return date.getHours() * 60 + date.getMinutes();
}

/** «HH:mm» из минут после локальной полуночи. */
function timeOf(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Чип события: title + время, стилизация по state, клик и иконка сделки. */
export function EventChip({ span, onEventClick }: EventChipProps): JSX.Element {
  const { event } = span;
  const canceled = event.state === 'CANCELED';
  const start = new Date(event.startAt);
  const end = new Date(event.endAt);
  // §6.3: пунктир на стыке локальной полночи — фрагмент начинается/кончается в 0/1440,
  // но исходное событие — нет (значит, граница — продолжение, а не реальное начало/конец).
  const continuesFromPreviousDay = span.startMin === 0 && localMinutesOf(start) !== 0;
  const continuesToNextDay = span.endMin === MINUTES_PER_DAY && localMinutesOf(end) !== 0;

  return (
    <Box
      data-testid="event-chip"
      aria-label={canceled ? 'отменено' : undefined}
      onClick={(ev) => {
        ev.stopPropagation();
        onEventClick?.(event);
      }}
      sx={{
        position: 'absolute',
        top: `${span.topPct}%`,
        height: `${span.heightPct}%`,
        left: `${span.leftPct}%`,
        width: `${span.widthPct}%`,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        px: 0.5,
        py: 0.25,
        bgcolor: canceled ? 'action.disabledBackground' : 'primary.main',
        color: canceled ? 'text.disabled' : 'primary.contrastText',
        opacity: canceled ? 0.5 : 1,
        textDecorationLine: canceled ? 'line-through' : 'none',
        borderTopStyle: continuesFromPreviousDay ? 'dashed' : 'none',
        borderBottomStyle: continuesToNextDay ? 'dashed' : 'none',
        borderTopWidth: continuesFromPreviousDay ? 1 : 0,
        borderBottomWidth: continuesToNextDay ? 1 : 0,
        borderRadius: 0.5,
        overflow: 'hidden',
        cursor: onEventClick ? 'pointer' : 'default',
      }}
    >
      <Typography noWrap sx={{ fontSize: '0.6875rem', lineHeight: 1.2 }}>
        {event.title}
      </Typography>
      <Typography noWrap sx={{ fontSize: '0.625rem', lineHeight: 1.2, opacity: 0.8 }}>
        {timeOf(localMinutesOf(start))} – {timeOf(localMinutesOf(end))}
      </Typography>
      {event.dealId ? (
        <MuiLink
          component={RouterLink}
          to={`/crm/deals/${event.dealId}`}
          aria-label="Сделка"
          title="Сделка"
          onClick={(ev) => ev.stopPropagation()}
          sx={{ color: 'inherit', display: 'inline-flex', flexShrink: 0 }}
        >
          <LinkIcon sx={{ fontSize: 12 }} />
        </MuiLink>
      ) : null}
    </Box>
  );
}
